// @flow

import * as React from "react";
import {css} from '@emotion/core';
import styled from '@emotion/styled';

import moment from "moment";
import {PrimaryAlternateBtn} from "../../../Core/components/Buttons";
import {fontSize} from "../../../Styles/text";
import colors from "../../../Styles/colors";
import colorTransition from "../../../Styles/animation";
import { breakpoints } from "../../../Styles/breakpoints";
import {event} from "../../../Helpers/ga";
import {APP_NAME} from "../../App";

let oneDay = 24 * 60 * 60 * 1000;

let extractor = new Date();

// Select dates start one week back
const startDate = Date.now() - (13 * oneDay);

const endDate = startDate + (54 * oneDay);

let availableDates = [];

for (let i = startDate; i <= endDate; i += oneDay) {
  extractor.setTime(i);
  availableDates.push([extractor.getFullYear(), extractor.getMonth(), extractor.getDate()]);
}

let dateSelect = css`
  margin: 0;
  padding: 0 !important;
  background: none;
  ${fontSize(4)};
  font-weight: bold;
  cursor: pointer;
  transition: ${colorTransition};
  
  &:hover {
    background: none;
    color: ${colors.accent};
    
    option {
      color: black;
    } 
  }
`;

let CalendarButton = styled(PrimaryAlternateBtn)`
  margin: 0;
  background: none;
  display: none !important;
  
  &:hover, &:focus, &:active {
    background: none;
  }
  
  @media(min-width: ${breakpoints.large}px) {
    display: inline-block !important;
  }

  @media(min-width: ${breakpoints.xxlarge}px) {
    display: none !important;
  }
  
  @media print {
    display: none;
  }
`;

type Props = {|
  year: number,
  month: number,
  date: number,
  onChange: Function,
  toggleCalendar: Function,
  isCalendarOpen: bool
|};

export default class DateSelect extends React.PureComponent<Props> {
  onChangeDate(to: SyntheticEvent<HTMLSelectElement>) {
    let { year, month, date, onChange } = this.props;
    let [tYear, tMonth, tDate] = to.currentTarget.value.split('-');

    event(
      APP_NAME,
      `Date Select | Change Date`,
      `${year}-${month}-${date} | ${tYear}-${tMonth}-${tDate}`
    );
    onChange(to);
  }

  getAvailableDates(): Array<array> {
    const oneDay = 24 * 60 * 60 * 1000;
    const requestedStart = new Date(this.props.year, this.props.month, this.props.date).setHours(0,0,0,0);

    // Available dates start from two weeks ago and end 55 days after that 
    let start = (new Date()).setHours(0,0,0,0) - (13 * oneDay);
    const end = start + (54 * oneDay);
    const availableDates = this.getDateTriplesBetween(start, end); 
   
    // Handle case where requested date is out of bounds of the available dates
    if (requestedStart < start || requestedStart > (end + oneDay)) {
      const startDate = new Date(requestedStart);
      return [[startDate.getFullYear(), startDate.getMonth(), startDate.getDate()]];
   }

    return availableDates; 
  }

  // Generates an array of dateTriples [year, month, date] 
  // between two given unix timestamps
  getDateTriplesBetween(start: number, end: number): Array<array> {
    const oneDay = 24 * 60 * 60 * 1000;
    let extractor = new Date();
    let dateTriples = [];
  
    for (let i = start; i <= end; i += oneDay) {
      extractor.setTime(i);
      dateTriples.push([extractor.getFullYear(), extractor.getMonth(), extractor.getDate()]);
    }
  
    return dateTriples;
  }

  render() {
    let { year, month, date, toggleCalendar, isCalendarOpen } = this.props;

    const availableDates = this.getAvailableDates();

    return <div>
      <select css={dateSelect} onChange={this.onChangeDate.bind(this)} value={[year, month, date].join('-')}>
        {
          availableDates.map(dateTriple => {
            let [y, m, d] = dateTriple;
            let value = dateTriple.join('-');
            let formatter = moment(new Date(y, m, d));

            return <option key={value} value={value}>
              {formatter.format('ddd, MMM D YYYY')}
            </option>
          })
        }
      </select>
      <CalendarButton onClick={toggleCalendar}>
        {!isCalendarOpen ? 'Calendar' : 'Close'}
      </CalendarButton>
    </div>;
  }
}